import React, { useMemo, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { isEmpty } from 'ramda'
import { useSelector } from 'hooks/redux'
import strings from 'utils/strings'

import {
  getDomainsList,
  getPtoTemplateDiff,
  getTemplateById,
} from 'selectors/powerTakeOff'

import { Row, Col, Loading } from 'components/patterns'
import TemplateDiffViewModal from '../../TemplateDiffViewModal'

import {
  DiffTemplateCard,
  StyledTable,
  PtoDomainElement,
  PtoDomainElementTitle,
  PtoDomainPopDetails,
  PtoDomainNoFragments,
} from './PtoTemplateDiffTable.style'

const PtoTemplateDiffTable = ({ selectedTemplateId }) => {
  const diffListByTemplateId = useSelector(getPtoTemplateDiff, {
    templateId: selectedTemplateId,
  })
  const domainsList = useSelector(getDomainsList)
  const selectedTemplate = useSelector(getTemplateById, {
    templateId: selectedTemplateId,
  })
  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [domainTitle, setDomainTitle] = useState('')
  const [filteredDiffList, setFilteredDiffList] = useState([])
  const hasSelectedTemplate = !isEmpty(selectedTemplate)
  const hasTemplateAndDiff =
    !isEmpty(diffListByTemplateId) && hasSelectedTemplate

  const handleCloseModal = () => {
    setModalIsOpen(false)
  }

  const renderSelectedCard = useMemo(
    () =>
      hasSelectedTemplate && (
        <DiffTemplateCard
          className={`template-${selectedTemplateId}-diff`}
          template={selectedTemplate}
          handleTemplateSelection={() => {}}
          selectedTemplateId={selectedTemplateId}
          defaultChecked={false}
          showSelectButton={false}
        />
      ),
    [hasSelectedTemplate, selectedTemplateId, selectedTemplate]
  )

  const handleViewDifferenceClick = useCallback(
    (domainName) => {
      const filteredList = diffListByTemplateId.filter(
        (fragment) => fragment.domainName === domainName
      )

      setFilteredDiffList(filteredList)
      setDomainTitle(domainName)
      setModalIsOpen(true)
    },
    [diffListByTemplateId]
  )

  const diffdomainNames = useMemo(() => {
    const domainNames = (diffListByTemplateId ?? [])
      .map((f) => f?.domainName)
      .filter((id) => id != null)
    return new Set(domainNames)
  }, [diffListByTemplateId])

  const onViewDetails = useCallback(
    (domainName) => () => {
      if (domainName) handleViewDifferenceClick(domainName)
    },
    [handleViewDifferenceClick]
  )

  const renderDomainsList = useMemo(
    () =>
      domainsList.map((domain) => {
        const hasFragments = !!domain?.name && diffdomainNames.has(domain.name)

        return (
          <PtoDomainElement key={domain.id}>
            <PtoDomainElementTitle>{domain.name}</PtoDomainElementTitle>

            {hasFragments ? (
              <PtoDomainPopDetails
                onClick={onViewDetails(domain.name)}
                data-testid="view-details-link"
              >
                {strings.pto_template_view_details}
              </PtoDomainPopDetails>
            ) : (
              <PtoDomainNoFragments>
                {strings.apply_pto_template_no_fragments}
              </PtoDomainNoFragments>
            )}
          </PtoDomainElement>
        )
      }),
    [domainsList, diffdomainNames, onViewDetails]
  )

  const columns = [
    {
      title: strings.setting_name,
      dataIndex: 'name',
      key: 'name',
      width: 500,
    },
    {
      title: strings.pto_value_on_vehicle,
      dataIndex: 'vehicleValue',
      key: 'vehicleValue',
      width: 250,
    },
    {
      title: strings.pto_value_on_template,
      dataIndex: 'templateValue',
      key: 'templateValue',
      width: 250,
    },
    {
      title: strings.refNo,
      dataIndex: 'refNo',
      key: 'refNo',
    },
  ]

  const diffListTable = hasTemplateAndDiff && (
    <StyledTable
      size="middle"
      columns={columns}
      dataSource={filteredDiffList}
      pagination={false}
      rowKey={(record) => record.refNo}
    />
  )

  return !hasSelectedTemplate ? (
    <Loading contained size="sm" />
  ) : (
    <Row className="diff-results-row">
      <Col span={24} className="diff-results-col">
        {renderSelectedCard}
        {renderDomainsList}

        {modalIsOpen && hasTemplateAndDiff && (
          <TemplateDiffViewModal
            onClose={handleCloseModal}
            diffListTable={diffListTable}
            domainTitle={domainTitle}
          />
        )}
      </Col>
    </Row>
  )
}

PtoTemplateDiffTable.propTypes = {
  selectedTemplateId: PropTypes.string,
}

export default PtoTemplateDiffTable
